import { useCallback, useEffect, useRef, useState } from 'react';
import { CartService } from '../../api/services/cartService';
import { EmailCheckResponse, EmailCheckStatus } from '../../types/cartApi';

// The address is asked about once the shopper stops typing for this long.
const TYPING_PAUSE_MS = 700;

export interface EmailCheck {
  // The domain takes no mail: no order can be placed on this address.
  notFound: boolean;
  // A likely typo the shopper has not answered yet.
  needsAnswer: boolean;
  // The address the shopper most likely meant, when there is one.
  suggestion?: string;
  // Grows each time a checkout is stopped at this address, so the cart can
  // bring the field into view.
  timesStopped: number;
  // Swap the typed address for the suggested one.
  accept: () => void;
  // The shopper says the typed address is right.
  keep: () => void;
  // Asks now when not asked yet. True: no order on this address as it is.
  stopsCheckout: () => Promise<boolean>;
  // The API refused an order on this address after the cart let it through.
  refuse: (suggestion?: string) => void;
}

// Asks the API about a checkout address: a domain that takes no mail, or a
// likely typo of a known provider. A request that fails stops nothing; the
// API refuses an address that takes no mail again on the order itself.
export function useEmailCheck({
  cartService,
  email,
  active,
  onAccept,
}: {
  cartService: CartService;
  email: string;
  // False while there is nothing to ask: no well-formed address yet, the
  // field is not shown, or the address is already proven.
  active: boolean;
  onAccept: (suggestion: string) => void;
}): EmailCheck {
  const address = email.trim();
  // One request per address, shared by the typing pause and the checkout.
  const requests = useRef(new Map<string, Promise<EmailCheckResponse | null>>());
  const [answer, setAnswer] = useState<{ address: string; result: EmailCheckResponse } | null>(null);
  const [keptAddress, setKeptAddress] = useState('');
  const [timesStopped, setTimesStopped] = useState(0);

  const ask = useCallback(
    (asked: string) => {
      let request = requests.current.get(asked);
      if (!request) {
        request = cartService.checkEmail(asked).catch(() => {
          // Forgotten, so the next pause or checkout asks again.
          requests.current.delete(asked);
          return null;
        });
        requests.current.set(asked, request);
      }
      return request;
    },
    [cartService],
  );

  useEffect(() => {
    if (!active) return;
    // An answer that comes back after the address changed is dropped.
    let stale = false;
    const timer = setTimeout(async () => {
      const result = await ask(address);
      if (!stale && result) setAnswer({ address, result });
    }, TYPING_PAUSE_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [active, address, ask]);

  const verdictOf = (result: EmailCheckResponse | null) => ({
    notFound: result?.status === EmailCheckStatus.DomainNotFound,
    needsAnswer: result?.status === EmailCheckStatus.Suggestion && keptAddress !== address,
  });

  const result = answer?.address === address ? answer.result : null;

  return {
    ...verdictOf(result),
    suggestion: result?.suggestion,
    timesStopped,
    accept: () => {
      if (result?.suggestion) onAccept(result.suggestion);
    },
    keep: () => setKeptAddress(address),
    stopsCheckout: async () => {
      if (!active) return false;
      const asked = await ask(address);
      if (!asked) return false;
      setAnswer({ address, result: asked });
      const { notFound, needsAnswer } = verdictOf(asked);
      const stops = notFound || needsAnswer;
      if (stops) setTimesStopped((times) => times + 1);
      return stops;
    },
    refuse: (suggestion) => {
      const refused = { status: EmailCheckStatus.DomainNotFound, suggestion };
      requests.current.set(address, Promise.resolve(refused));
      setAnswer({ address, result: refused });
      setTimesStopped((times) => times + 1);
    },
  };
}
